import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/layout/AppLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Search, Clock, CheckCircle2, Send, Bell, X,
  User, Calendar, ArrowRight, Filter, FileText, Play,
} from 'lucide-react';

/* ═══ MOCK DATA ═══ */
const SEGNALAZIONI = [
  { id: 'SGN-2026-042', data: '17/04/2026', ora: '09:40', nome: 'Marchetti', cognome: 'Lucia', cf: 'MRCLCU50A45H501M', tipologia: 'Attivazione ADI', stato: 'bozza', draftStep: 1, draftData: { pazCognome: 'Marchetti', pazNome: 'Lucia', pazCf: 'MRCLCU50A45H501M', pazDataNascita: '15/01/1950', caregiverNome: 'Marchetti Franco', caregiverTel: '+39 06 1234567', caregiverRel: 'Figlio', vive: 'Con familiare', assistitoDa: [] } },
  { id: 'SGN-2026-041', data: '16/04/2026', ora: '14:22', nome: 'Moretti', cognome: 'Giulia', cf: 'MRTGLA47B45H501F', tipologia: 'Attivazione ADI', stato: 'inviata' },
  { id: 'SGN-2026-040', data: '15/04/2026', ora: '10:15', nome: 'Russo', cognome: 'Giuseppe', cf: 'RSSGPP41H15H501R', tipologia: 'Attivazione ADI', stato: 'in-lavorazione' },
  { id: 'SGN-2026-039', data: '14/04/2026', ora: '16:40', nome: 'Ferrari', cognome: 'Anna', cf: 'FRRNNA54S65H501T', tipologia: 'Supporto Sociale', stato: 'in-lavorazione' },
  { id: 'SGN-2026-038', data: '12/04/2026', ora: '09:05', nome: 'Colombo', cognome: 'Franco', cf: 'CLMFNC36C12H501P', tipologia: 'Attivazione ADI', stato: 'accettata' },
  { id: 'SGN-2026-037', data: '10/04/2026', ora: '11:30', nome: 'Galli', cognome: 'Teresa', cf: 'GLLTRS58D45H501K', tipologia: 'Attivazione ADI', stato: 'accettata' },
  { id: 'SGN-2026-036', data: '08/04/2026', ora: '15:10', nome: 'Ricci', cognome: 'Maria', cf: 'RCCMRA43A65H501L', tipologia: 'Supporto Sociale', stato: 'accettata' },
  { id: 'SGN-2026-035', data: '05/04/2026', ora: '08:45', nome: 'Santini', cognome: 'Paolo', cf: 'SNTPLA49G15H501V', tipologia: 'Attivazione ADI', stato: 'inviata' },
  { id: 'SGN-2026-034', data: '03/04/2026', ora: '13:20', nome: 'Leone', cognome: 'Carla', cf: 'LNCCRL45E65H501N', tipologia: 'Attivazione ADI', stato: 'accettata' },
  { id: 'SGN-2026-033', data: '01/04/2026', ora: '10:00', nome: 'Baldi', cognome: 'Roberto', cf: 'BLDRRT52M15H501Z', tipologia: 'Supporto Sociale', stato: 'in-lavorazione' },
];

const NOTIFICHE = [
  { id: 1, testo: 'La richiesta per Russo Giuseppe è stata presa in carico dalla COT.', tempo: '2h fa', tipo: 'transizione' },
  { id: 2, testo: 'Richiesta per Colombo Franco accettata — PAI in fase di pianificazione.', tempo: '4h fa', tipo: 'transizione' },
  { id: 3, testo: 'La segnalazione per Ferrari Anna è in lavorazione presso la COT Roma 2.', tempo: '1g fa', tipo: 'transizione' },
  { id: 4, testo: 'Richiesta per Galli Teresa completata — Erogazione ADI avviata.', tempo: '2g fa', tipo: 'transizione' },
];

const STATO_STYLE = {
  bozza: { bg: 'bg-blue-50 dark:bg-blue-950/30', text: 'text-blue-700 dark:text-blue-400', dot: 'bg-blue-400', label: 'Bozza' },
  inviata: { bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-600 dark:text-slate-300', dot: 'bg-slate-400', label: 'Inviata' },
  'in-lavorazione': { bg: 'bg-amber-50 dark:bg-amber-950/30', text: 'text-amber-700 dark:text-amber-400', dot: 'bg-amber-500', label: 'In lavorazione COT' },
  accettata: { bg: 'bg-emerald-50 dark:bg-emerald-950/30', text: 'text-emerald-700 dark:text-emerald-400', dot: 'bg-emerald-500', label: 'Accettata' },
};

export default function PuaDashboard() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filterStato, setFilterStato] = useState('tutti');
  const [showNotifiche, setShowNotifiche] = useState(false);

  const filtered = SEGNALAZIONI.filter(s => {
    const matchSearch = `${s.nome} ${s.cognome} ${s.cf} ${s.id} ${s.tipologia}`.toLowerCase().includes(search.toLowerCase());
    const matchStato = filterStato === 'tutti' || s.stato === filterStato;
    return matchSearch && matchStato;
  });

  const countBozza = SEGNALAZIONI.filter(s => s.stato === 'bozza').length;
  const countInviata = SEGNALAZIONI.filter(s => s.stato === 'inviata').length;
  const countLavorazione = SEGNALAZIONI.filter(s => s.stato === 'in-lavorazione').length;
  const countAccettata = SEGNALAZIONI.filter(s => s.stato === 'accettata').length;

  return (
    <AppLayout role="pua">
      <div className="p-4 md:p-6 max-w-[1400px] mx-auto space-y-6" data-testid="pua-dashboard">

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Le mie Segnalazioni</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Punto Unico di Accesso — Gestione richieste ADI e supporto sociale</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Notifiche Badge */}
            <button
              onClick={() => setShowNotifiche(prev => !prev)}
              className="relative w-10 h-10 rounded-xl border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              data-testid="btn-notifiche"
            >
              <Bell className="w-4.5 h-4.5 text-slate-500" />
              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-blue-500 text-white text-[9px] font-bold flex items-center justify-center">{NOTIFICHE.length}</span>
            </button>
            {/* CTA Nuova Richiesta */}
            <Button
              className="gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 h-10"
              onClick={() => navigate('/pua/nuova-richiesta')}
              data-testid="btn-nuova-richiesta"
            >
              <Plus className="w-4 h-4" /> Nuova Richiesta ADI
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-3 gap-4" data-testid="pua-kpi">
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700 p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Inviate</span>
              <Send className="w-4 h-4 text-slate-400" />
            </div>
            <p className="text-3xl font-bold text-slate-700 dark:text-slate-200">{countInviata}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">In attesa di presa in carico COT</p>
          </div>
          <div className="bg-amber-50 dark:bg-amber-950/20 rounded-2xl border border-amber-200 dark:border-amber-800/40 p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">In Lavorazione</span>
              <Clock className="w-4 h-4 text-amber-400" />
            </div>
            <p className="text-3xl font-bold text-amber-700 dark:text-amber-400">{countLavorazione}</p>
            <p className="text-[10px] text-amber-500 dark:text-amber-400/70 mt-0.5">La COT sta processando la richiesta</p>
          </div>
          <div className="bg-emerald-50 dark:bg-emerald-950/20 rounded-2xl border border-emerald-200 dark:border-emerald-800/40 p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Accettate</span>
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            </div>
            <p className="text-3xl font-bold text-emerald-700 dark:text-emerald-400">{countAccettata}</p>
            <p className="text-[10px] text-emerald-500 dark:text-emerald-400/70 mt-0.5">Richieste approvate e attivate</p>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Cerca per nome, CF o ID segnalazione..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 h-10 rounded-xl text-sm"
              data-testid="pua-search"
            />
          </div>
          <div className="flex items-center gap-1.5">
            {[
              { key: 'tutti', label: `Tutti (${SEGNALAZIONI.length})` },
              { key: 'bozza', label: `Bozze (${countBozza})` },
              { key: 'inviata', label: `Inviate (${countInviata})` },
              { key: 'in-lavorazione', label: `In lavorazione (${countLavorazione})` },
              { key: 'accettata', label: `Accettate (${countAccettata})` },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilterStato(f.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterStato === f.key ? 'bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
                data-testid={`filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Data Grid */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden" data-testid="pua-segnalazioni-table">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700/60">
                  {['Data Richiesta', 'Paziente', 'Tipologia', 'Stato', ''].map((col, i) => (
                    <th key={i} className="px-5 py-3 text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={5} className="px-5 py-12 text-center text-sm text-slate-400">Nessuna segnalazione trovata</td></tr>
                ) : (
                  filtered.map(s => {
                    const st = STATO_STYLE[s.stato];
                    return (
                      <tr key={s.id} className="border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors" data-testid={`row-${s.id}`}>
                        <td className="px-5 py-4">
                          <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{s.data}</p>
                          <p className="text-[10px] text-slate-400">{s.ora} &middot; {s.id}</p>
                        </td>
                        <td className="px-5 py-4">
                          <p className="text-sm font-semibold text-slate-800 dark:text-white">{s.cognome} {s.nome}</p>
                          <p className="text-[10px] text-slate-400 font-mono">{s.cf}</p>
                        </td>
                        <td className="px-5 py-4">
                          <Badge className="bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-0 text-[10px] font-semibold">{s.tipologia}</Badge>
                        </td>
                        <td className="px-5 py-4">
                          <Badge className={`${st.bg} ${st.text} border-0 text-[10px] font-semibold gap-1`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                            {st.label}
                          </Badge>
                        </td>
                        <td className="px-5 py-4">
                          {s.stato === 'bozza' && (
                            <button
                              onClick={() => navigate('/pua/nuova-richiesta', { state: { draftData: s.draftData, draftStep: s.draftStep } })}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400 text-xs font-semibold hover:bg-emerald-50 dark:hover:bg-emerald-950/20 transition-colors"
                              data-testid={`resume-btn-${s.id}`}
                            >
                              <Play className="w-3 h-3" /> Riprendi compilazione
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ═══ PANNELLO NOTIFICHE ═══ */}
      <AnimatePresence>
        {showNotifiche && (
          <div className="fixed inset-0 z-50" data-testid="notifiche-panel">
            <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setShowNotifiche(false)} />
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
              className="absolute top-0 right-0 h-full w-full max-w-[400px] bg-white dark:bg-slate-900 shadow-2xl border-l border-slate-200 dark:border-slate-800 flex flex-col"
            >
              <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4 text-blue-500" />
                  <h2 className="text-sm font-bold text-slate-800 dark:text-white">Aggiornamenti Pratiche</h2>
                </div>
                <button onClick={() => setShowNotifiche(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" data-testid="notifiche-close">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                {NOTIFICHE.map(n => (
                  <div key={n.id} className="px-6 py-4 border-b border-slate-100 dark:border-slate-800/60 last:border-b-0" data-testid={`notifica-${n.id}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center shrink-0 mt-0.5">
                        <ArrowRight className="w-3.5 h-3.5 text-blue-500" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-slate-700 dark:text-slate-200 leading-relaxed">{n.testo}</p>
                        <p className="text-[10px] text-slate-400 mt-1">{n.tempo}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </AppLayout>
  );
}
